import { HEROES, Team, type HeroId, type PlayerStats } from "@rivalwatch/shared";
import { RoleIcon } from "./icons.tsx";
import { HeroPortrait } from "./Portrait.tsx";
import { ui } from "./store.ts";

export interface BoardPlayer {
  key: string | number;
  name: string;
  hero: HeroId;
  stats: Omit<PlayerStats, "objectiveTicks">;
  me?: boolean;
  dead?: boolean;
  /** Last column: ping on the live board, time on point after the match. */
  tail: string;
  tailDim?: boolean;
}

type Stats = BoardPlayer["stats"];
export interface StatCol {
  label: string;
  /** Full name for screen readers when the label is an abbreviation. */
  title?: string;
  value: (s: Stats) => number;
}

/** Rivals order: KOs (kills you took part in), deaths, assists, then output. */
const KDA: StatCol[] = [
  { label: "KO", title: "KOs", value: (s) => s.kills },
  { label: "D", title: "Deaths", value: (s) => s.deaths },
  { label: "A", title: "Assists", value: (s) => s.assists },
];
const OUTPUT: StatCol[] = [
  { label: "Damage", value: (s) => s.damage },
  { label: "Healing", value: (s) => s.healing },
  { label: "Blocked", value: (s) => s.mitigated },
];
export const FINAL_HITS: StatCol = { label: "Final hits", value: (s) => s.finalHits };

/** Eliminations a team caused: one Final Hit per enemy KO'd. */
export const eliminations = (players: { stats: Stats }[]) => players.reduce((n, p) => n + p.stats.finalHits, 0);

/** One team's panel of slanted portrait rows (Marvel Rivals style). `extra` columns follow the assists. */
export function TeamBoard({ friend, note, tailLabel, players, extra = [] }: { friend: boolean; note: string; tailLabel: string; players: BoardPlayer[]; extra?: StatCol[] }) {
  const title = friend ? "Your team" : "Enemy team";
  const cols = [...KDA, ...extra, ...OUTPUT];
  return (
    <section class={`rv-board-team ${friend ? "ally" : "foe"}`} role="table" aria-label={title} style={{ "--wide": String(OUTPUT.length + extra.length) }}>
      <header>
        <b>{title}</b>
        <span>{note}</span>
      </header>
      <div class="rv-board-head" role="row">
        <span role="columnheader" aria-label="Hero" />
        <span role="columnheader">Player</span>
        {cols.map((c) => (
          <span key={c.label} role="columnheader" title={c.title} aria-label={c.title}>
            {c.label}
          </span>
        ))}
        <span role="columnheader">{tailLabel}</span>
      </div>
      {players.map((p) => (
        <div key={p.key} class={`rv-board-row${p.me ? " me" : ""}${p.dead ? " dead" : ""}`} role="row">
          <span role="cell">
            <HeroPortrait hero={p.hero} />
          </span>
          <span class="rv-board-name" role="cell">
            <b>{p.name}</b>
            <small>
              <RoleIcon role={HEROES[p.hero].role} size={13} />
              {HEROES[p.hero].name}
            </small>
          </span>
          {cols.map((c) => (
            <span key={c.label} class="num" role="cell">
              {Math.round(c.value(p.stats)).toLocaleString()}
            </span>
          ))}
          <span class={`num${p.tailDim ? " dim" : ""}`} role="cell">
            {p.tail}
          </span>
        </div>
      ))}
    </section>
  );
}

/** Hold-Tab scoreboard: your team, then the enemy team. */
export function Scoreboard() {
  const l = ui.lobby.value;
  if (!l) return null;
  const me = l.players.find((p) => p.id === ui.you.value);
  const myTeam = me?.team ?? Team.A;
  const m = ui.hud.value?.match;
  const alive = new Map((ui.hud.value?.roster ?? []).map((r) => [r.id, r.alive]));
  return (
    <div class="rv-board-wrap" aria-label="Scoreboard">
      <div class="rv-board">
        {[myTeam, myTeam === Team.A ? Team.B : Team.A].map((team, ti) => {
          const players = l.players.filter((p) => p.team === team);
          return (
            <TeamBoard
              friend={team === myTeam}
              note={`${m ? `${Math.floor(m.progress[ti]!)}% · ` : ""}${eliminations(players)} eliminations`}
              tailLabel="Ping"
              players={players.map((p) => ({
                key: p.id,
                name: p.name,
                hero: p.hero,
                stats: p.stats,
                me: p.id === ui.you.value,
                dead: alive.get(p.id) === false,
                tail: p.bot ? "BOT" : String(p.ping),
                tailDim: true,
              }))}
            />
          );
        })}
      </div>
    </div>
  );
}
